import React, { Component, Suspense, lazy } from 'react';
import RouteFallback from 'components/shared/loader/RouteFallback';

const RELOADED_KEY = 'spkl_chunk_reload';

// A code-split route can fail to load: the network drops, or a deploy replaced the chunk file the open tab still
// points at. Reload the page ONCE (fetches the new index.html); if it fails again, say so instead of a blank page.
class ChunkErrorBoundary extends Component {
  state = { failed: false };

  static getDerivedStateFromError() {
    return { failed: true };
  }

  componentDidCatch(error) {
    const isChunkError = /dynamically imported module|ChunkLoadError|Loading chunk|Importing a module script failed/i.test(
      `${error && error.name} ${error && error.message}`
    );
    if (!isChunkError) throw error;
    try {
      if (!window.sessionStorage.getItem(RELOADED_KEY)) {
        window.sessionStorage.setItem(RELOADED_KEY, '1');
        window.location.reload();
      }
    } catch (e) {
      // sessionStorage unavailable: fall through to the message below
    }
  }

  render() {
    if (!this.state.failed) return this.props.children;
    return (
      <div
        className="chunk-load-error"
        role="alert"
        style={{ padding: 40, textAlign: 'center' }}
      >
        <p>
          This page could not be loaded. Check your connection and try again.
        </p>
        <button
          type="button"
          className="btn btn-default"
          onClick={() => window.location.reload()}
        >
          Reload
        </button>
      </div>
    );
  }
}

/**
 * Route-level code splitting. `lazyComponent(() => import('./Foo'))` returns a component that downloads Foo's chunk
 * the first time it renders (showing the app's usual spinner meanwhile) and can be used anywhere the eager component
 * was, including `<Route component={...}>`. The module must have a default export.
 */
export default function lazyComponent(loader) {
  const LazyInner = lazy(loader);
  const LazyComponent = (props) => (
    <ChunkErrorBoundary>
      <Suspense fallback={<RouteFallback />}>
        <LazyInner {...props} />
      </Suspense>
    </ChunkErrorBoundary>
  );
  LazyComponent.preload = loader;
  return LazyComponent;
}
